import React, { useMemo, useState, useCallback, useEffect } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { useGameStore } from '../../store/gameStore';
import { Network, User, Skull } from 'lucide-react';
import { SuspectDetailModal } from './SuspectDetailModal';
import { isInterviewAction } from '../../utils/actionHelpers';

// Custom node component for the relationship map
const PersonNode = ({ data }) => {
  const isVictim = data.isVictim;
  const isSelected = data.selected;
  const isInterviewed = data.isInterviewed;

  return (
    <div
      className={`
        relative p-3 rounded-lg shadow-lg cursor-pointer transition-all duration-200
        ${isVictim
          ? 'bg-red-950 border-2 border-red-500'
          : 'bg-slate-800 border-2 border-slate-600'
        }
        ${isSelected ? 'ring-2 ring-white' : ''}
        hover:scale-105
      `}
      style={{ minWidth: '120px' }}
    >
      {/* Connection handles */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-2 !h-2 !bg-transparent !border-0"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-2 !h-2 !bg-transparent !border-0"
      />
      <Handle
        type="target"
        position={Position.Left}
        className="!w-2 !h-2 !bg-transparent !border-0"
      />
      <Handle
        type="source"
        position={Position.Right}
        className="!w-2 !h-2 !bg-transparent !border-0"
      />

      <div className="flex flex-col items-center gap-2">
        {data.image ? (
          <img
            src={data.image}
            alt={data.label}
            className={`
              w-14 h-14 rounded-full object-cover border-2
              ${isVictim ? 'border-red-500' : 'border-slate-500'}
            `}
          />
        ) : (
          <div
            className={`
              w-14 h-14 rounded-full flex items-center justify-center
              ${isVictim ? 'bg-red-900' : 'bg-slate-700'}
            `}
          >
            {isVictim ? (
              <Skull className="w-7 h-7 text-red-400" />
            ) : (
              <User className="w-7 h-7 text-slate-400" />
            )}
          </div>
        )}
        <div className="text-center">
          <p className="text-sm font-bold text-white">{data.label}</p>
          {isVictim && (
            <p className="text-[10px] text-red-400 font-semibold">VICTIM</p>
          )}
          {!isVictim && data.occupation && (
            <p className="text-[10px] text-gray-400 truncate max-w-[100px]">{data.occupation}</p>
          )}
        </div>
      </div>

      {/* Interview indicator */}
      {!isVictim && isInterviewed && (
        <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border border-white" title="Interviewed" />
      )}

      {/* Unknown relationship indicator */}
      {!isVictim && !isInterviewed && (
        <div className="absolute -top-1 -right-1 w-3 h-3 bg-gray-500 rounded-full border border-white" title="Not yet interviewed" />
      )}
    </div>
  );
};

const nodeTypes = {
  person: PersonNode,
};

export const RelationshipMap = ({ open, onClose }) => {
  const {
    suspects,
    discovered,
    selectedTheme,
    caseData,
    messageHistory,
    setPreSelectedAction,
    gameStatus,
  } = useGameStore();
  // A won/lost case can no longer be questioned; withholding onInterview
  // hides the Interview buttons, as on the Suspect Wall (PG-21).
  const isGameOver = gameStatus === 'won' || gameStatus === 'lost';

  const [selectedSuspect, setSelectedSuspect] = useState(null);

  const themeAccent = {
    noir: '#DC143C',
    '90s_nyc': '#FF6B35',
    miami_vice: '#FF1493',
  }[selectedTheme || 'noir'];

  // Check if suspect has been interviewed or interrogated
  const checkInterviewed = useCallback((suspectId) => {
    return messageHistory.some(
      msg => isInterviewAction(msg.action) && msg.target === suspectId && msg.narrative
    );
  }, [messageHistory]);

  // Build nodes and edges for the relationship map
  const { initialNodes, initialEdges } = useMemo(() => {
    const nodes = [];
    const edges = [];

    // Center position for victim
    const centerX = 400;
    const centerY = 300;
    const radius = 280;

    // Add victim node at center
    if (caseData?.victim) {
      nodes.push({
        id: 'victim',
        type: 'person',
        position: { x: centerX - 60, y: centerY - 60 },
        data: {
          label: caseData.victim.name,
          image: caseData.victim.portrait,
          isVictim: true,
          occupation: caseData.victim.occupation,
        },
        draggable: true,
      });
    }

    // Add discovered suspects in a circle around victim (with defensive checks)
    const discoveredSuspects = (suspects || []).filter(s => discovered?.suspects?.includes(s.id));
    const angleStep = (2 * Math.PI) / Math.max(discoveredSuspects.length, 1);

    discoveredSuspects.forEach((suspect, idx) => {
      const angle = angleStep * idx - Math.PI / 2; // Start from top
      const x = centerX + radius * Math.cos(angle) - 60;
      const y = centerY + radius * Math.sin(angle) - 60;

      const interviewed = checkInterviewed(suspect.id);

      nodes.push({
        id: `suspect-${suspect.id}`,
        type: 'person',
        position: { x, y },
        data: {
          label: suspect.name,
          image: suspect.portrait,
          occupation: suspect.occupation,
          suspectId: suspect.id,
          suspect: suspect,
          isInterviewed: interviewed,
        },
        draggable: true,
      });

      // Add edge from suspect to victim
      if (caseData?.victim) {
        // Only show relationship if interviewed, otherwise show "?"
        const relationshipLabel = interviewed
          ? (suspect.relationship || 'Unknown relationship')
          : '?';

        edges.push({
          id: `edge-${suspect.id}-victim`,
          source: `suspect-${suspect.id}`,
          target: 'victim',
          label: relationshipLabel,
          style: {
            stroke: interviewed ? themeAccent : '#6b7280',
            strokeWidth: interviewed ? 2 : 1,
            strokeDasharray: interviewed ? undefined : '5,5',
          },
          labelStyle: {
            fill: '#ffffff',
            fontSize: 11,
            fontWeight: 500,
          },
          labelBgStyle: {
            fill: interviewed ? '#1e293b' : '#374151',
            fillOpacity: 1,
          },
          labelBgPadding: [6, 10],
          labelBgBorderRadius: 4,
          type: 'smoothstep',
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: interviewed ? themeAccent : '#6b7280',
            width: 15,
            height: 15,
          },
        });
      }
    });

    return { initialNodes: nodes, initialEdges: edges };
  }, [suspects, discovered, caseData, themeAccent, checkInterviewed]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Update nodes and edges when data changes (e.g., after interview)
  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  // Handle node click
  const onNodeClick = useCallback((event, node) => {
    if (node.data.suspect) {
      setSelectedSuspect(node.data.suspect);
    }
  }, []);

  // Get interview/interrogation history for selected suspect (memoized)
  const getInterviewHistory = useCallback((suspectId) => {
    return messageHistory.filter(
      msg => isInterviewAction(msg.action) && msg.target === suspectId && msg.narrative
    );
  }, [messageHistory]);

  const handleInterviewClick = (suspectId) => {
    setPreSelectedAction('interview', suspectId);
  };

  const discoveredSuspects = (suspects || []).filter(s => discovered?.suspects?.includes(s.id));

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-[95vw] w-[95vw] lg:max-w-[85vw] lg:w-[85vw] h-[85vh] lg:h-[90vh] p-0 bg-slate-900 border-2" style={{ borderColor: themeAccent }}>
          <div className="flex flex-col h-full">
            <DialogHeader className="px-4 py-3 border-b border-white/10">
              <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
                <Network className="w-5 h-5" style={{ color: themeAccent }} />
                Relationship Map
                <span className="text-sm font-normal text-gray-400">
                  ({discoveredSuspects.length} suspects)
                </span>
              </DialogTitle>
            </DialogHeader>

            {discoveredSuspects.length === 0 ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center text-gray-400">
                  <Network className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No suspects discovered yet</p>
                  <p className="text-sm mt-2">Investigate to discover persons of interest</p>
                </div>
              </div>
            ) : (
              <div className="flex-1">
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onNodeClick={onNodeClick}
                  nodeTypes={nodeTypes}
                  fitView
                  className="bg-slate-900"
                >
                  <Background
                    variant="dots"
                    gap={20}
                    size={1}
                    color={`${themeAccent}20`}
                  />
                  <Controls showInteractive={false} />
                  <MiniMap
                    nodeColor={(node) => {
                      if (node.data?.isVictim) return '#ef4444';
                      if (node.data?.isInterviewed) return themeAccent;
                      return '#4b5563';
                    }}
                    maskColor="rgba(0, 0, 0, 0.8)"
                    style={{ backgroundColor: '#1e293b' }}
                  />
                </ReactFlow>
              </div>
            )}

            {/* Footer */}
            <div className="px-4 py-2 border-t border-white/10 bg-black/40">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-400">
                  Click suspects to view details. Drag to rearrange. Interview suspects to reveal relationships.
                </p>
                <div className="flex items-center gap-4 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    <span className="text-gray-400">Interviewed</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-gray-500"></div>
                    <span className="text-gray-400">Not interviewed</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Suspect Detail Modal */}
      {selectedSuspect && (
        <SuspectDetailModal
          suspect={selectedSuspect}
          open={!!selectedSuspect}
          onClose={() => setSelectedSuspect(null)}
          onInterview={isGameOver ? undefined : handleInterviewClick}
          interviewHistory={getInterviewHistory(selectedSuspect.id)}
          hasBeenInterviewed={checkInterviewed(selectedSuspect.id)}
        />
      )}
    </>
  );
};

export default RelationshipMap;
